import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import { NumOrdersOption } from "@redotech/redo-return-core-common/return-flow/condition-enums";
import type {
  DateSubconditionState,
  NumOrdersState,
} from "@redotech/redo-return-core-common/return-flow/condition-validate";
import { ConditionTypeDetailsProps } from "@redotech/redo-return-ui/flows/return-flow/condition";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { memo, useMemo } from "react";
import { DateSubconditionSelector } from "./date-subcondition-selector";

export const NumOrdersDetails = memo(function NumOrdersDetails({
  state,
  setState,
  disabled,
}: ConditionTypeDetailsProps<NumOrdersState>) {
  const { t } = useTranslation();
  const orderOptions: RedoListItem<NumOrdersOption>[] = useMemo(
    () =>
      Object.values(NumOrdersOption).map((option) => ({
        id: option,
        value: option,
        type: "text" as const,
        text:
          option === NumOrdersOption.LESS_THAN
            ? t("Less than")
            : t("Greater than"),
      })),
    [t],
  );

  const selectedOption = state.orderOption
    ? orderOptions.find((option) => option.value === state.orderOption)
    : undefined;

  // Handler for date subcondition changes
  const handleDateSubconditionChange = useHandler(
    (updatedDateSubcondition: DateSubconditionState) => {
      setState({ ...state, dateSubcondition: updatedDateSubcondition });
    },
  );

  return (
    <>
      <RedoSingleSelectDropdownInput
        disabled={disabled}
        flexGrow
        label={t("Comparison")}
        options={orderOptions}
        optionSelected={({ item }) =>
          setState({ ...state, orderOption: item.value })
        }
        placeholder={t("Select comparison...")}
        selectedItem={selectedOption}
        size="xs"
      />
      <RedoTextInput
        label={t("Number of orders")}
        min={0}
        onBlur={() =>
          setState({ ...state, numOrders: (+state.numOrders).toFixed(0) })
        }
        setValue={(e) => setState({ ...state, numOrders: e })}
        state={disabled ? "readonly" : "default"}
        value={state.numOrders}
      />

      {/* Use shared date subcondition selector */}
      <DateSubconditionSelector
        disabled={disabled}
        onChange={handleDateSubconditionChange}
        state={state.dateSubcondition}
      />
    </>
  );
});
